import { createContext, useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { isValid, startOfDay, format, parseISO } from 'date-fns';

import { PromotionSuiteVoucherStatus, VoucherType as VoucherTypeEnum } from '@nf/achievement-system-v2';
import { fifaCwcLeagueIds } from '@/settings/league-settings';
import { useBetslipStore } from '@/store/betslip';
import { useBetslipContext } from '@/features/betslip/contexts/betslip-context';
import {
	createFreeBetVoucherConditionMap,
	getFreeBetConditionKey,
	getWillBeUsedVouchers,
	RewardUseVoucherEnum,
	useFreebetVoucherCondition,
} from '@/hooks/query/use-reward-system';
import { useAccountInfo } from '@/hooks/query/account';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import {
	validateFreeBetCondition,
	type FreeBetVoucherConditionForValidation,
} from '@/features/betslip/voucher/hooks/use-free-bet-voucher';

type VoucherInfo = {
	Id: string;
	Quota: number;
	ExpireDate: string;
	VoucherType: number;
	VoucherDetailId: number;
	VoucherSystemVersion: number;
	PromotionInfo?: {
		Id: number;
		ResourceKey: string | null;
		ResourceName: string | null;
		ShowHint: boolean;
		PromotionType: number;
	};
};

interface VoucherContextType {
	vouchers: Array<VoucherWithSupportStatus>;
	freeBetConditionMap: Map<string, FreeBetVoucherConditionForValidation>;
}

export type VoucherWithSupportStatus = VoucherInfo & {
	isEnabled: boolean;
	expireDateDisplay: string;
	disableMessage: string | null;
	/** 僅 Free bet 條件驗證：多筆失敗代碼 */
	disableMessages?: string[];
	disableMessageData?: {
		minOdds?: number;
		maxOdds?: number;
		oddsType?: number;
	};
};

type VoucherAvailability = {
	isEnabled: boolean;
	disableMessage: string | null;
};

const formatExpireDateForDisplay = (expireDate: string): string => {
	try {
		const hasDateTime = expireDate.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
		if (hasDateTime) {
			const [, year, month, day, hours, minutes] = hasDateTime;
			return `${year}/${month}/${day} ${hours}:${minutes} (GMT-4)`;
		}

		const date = parseISO(expireDate);
		if (!isValid(date)) return '';
		return `${format(date, 'yyyy/MM/dd HH:mm')} (GMT-4)`;
	} catch {
		return '';
	}
};

/** 依 VoucherType 分組；組間依該組最早到期時間排序。 */
const sortVouchers = (
	items: VoucherWithSupportStatus[]
): VoucherWithSupportStatus[] => {
	if (items.length <= 1) return items;

	const normalizeDateForSorting = (dateStr: string): number => {
		const date = new Date(dateStr);
		if (!isValid(date)) {
			return Infinity; // 無效日期排到最後
		}
		return startOfDay(date).getTime();
	};

	const groupedByVoucherType = items.reduce((groups, item, index) => {
		const type = item.VoucherType;
		if (!groups[type]) groups[type] = [];
		groups[type].push({ item, originalIndex: index });
		return groups;
	}, {} as Record<number, Array<{ item: VoucherWithSupportStatus; originalIndex: number }>>);

	const sortedGroupsByExpireDate = (Object.entries(groupedByVoucherType) as Array<
		[string, Array<{ item: VoucherWithSupportStatus; originalIndex: number }>]
	>)
		.map(([type, group]) => ({
			type: Number(type),
			group,
			minExpireMs: Math.min(
				...group.map(({ item }) => {
					const date = new Date(item.ExpireDate);
					return isValid(date) ? date.getTime() : Infinity;
				})
			)
		}))
		.sort((a, b) => a.minExpireMs - b.minExpireMs);

	return sortedGroupsByExpireDate.flatMap(({ group }) =>
		group
			.sort((a, b) => {
				const normalizedDateA = normalizeDateForSorting(a.item.ExpireDate);
				const normalizedDateB = normalizeDateForSorting(b.item.ExpireDate);
				return normalizedDateA !== normalizedDateB 
					? normalizedDateA - normalizedDateB 
					: a.originalIndex - b.originalIndex;
			})
			.map(({ item }) => item)
	);
};

const VoucherContext = createContext<VoucherContextType | undefined>(undefined);

export const VoucherContextProvider = ({
	children
}: {
	children: React.ReactNode;
}) => {
	const [
		currentMultiTicketModel
	] = useBetslipStore(state => [
		state.ticketCarts['normal'].multiTickets,
	]);
	const [shouldFetchFreeBetVoucherCondition, setShouldFetchFreeBetVoucherCondition] = useState(false);

	// call api to get free bet voucher condition
	const {
		data: freeBetVoucherConditionData = [],
		isFetched: isFreeBetConditionFetched,
	} = useFreebetVoucherCondition(
		shouldFetchFreeBetVoucherCondition
	);
		
	const freeBetConditionMap = useMemo(
		() => createFreeBetVoucherConditionMap(freeBetVoucherConditionData),
		[freeBetVoucherConditionData]
	);
	const ticket = currentMultiTicketModel[0];
	const [rawVouchers, setRawVouchers] = useState<Array<VoucherInfo>>([]);
	const { enableRewardSystem, oddsType } = useBetslipContext();
	const { user } = useAccountInfo();
	const enableFreeBetVoucher = useEnableFreeBetVoucher();
	const isDisableVoucher =
		[116, 134].includes(ticket.SportType) || Number(ticket.Bettype) === 10;

	const isVoucherSupportedSport = [1, 2, 993, 997].includes(ticket.SportType); // 10 
	const isVoucherCommonBaseCondition = ['1', '3', '7', '8'].includes(ticket.Bettype);
	const isNewRiskFreeBetVoucherSupported = !['1', '3', '7', '8'].includes(ticket.Bettype); // 10 lbl_newRFBhint01

	// 1   Valid for selected Soccer leagues (HDP/OU only) lbl_Obphint01
	const canUseOddsBoostVoucher = ticket.SportType === 1 &&
		isVoucherCommonBaseCondition &&
		[1, 2, 100, 102, 153].includes(ticket.LeagueGroupId);

	// 7 Valid for selected Soccer leagues (HDP/OU only) lbl_Obphint01
	const canUsePromotionOddsBoost = useMemo(() => {
		return user  
			&& user?.PromotionOddsBoostInfo?.EnablePromotionOddsBoost === true
			&& user?.PromotionOddsBoostInfo?.GiftOddsBoostTicketCount !== undefined
			&& ticket.LeagueId === 3
			&& isVoucherCommonBaseCondition && isVoucherSupportedSport;
	}, [user?.PromotionOddsBoostInfo, ticket.LeagueId, isVoucherCommonBaseCondition, isVoucherSupportedSport]);

	// 11 Valid for selected Soccer leagues (HDP/OU only) , lbl_Obphint01
	const canUseAffPromotionOddsBoost = useMemo(() => {
		return user  
			&& user?.PromotionOddsBoostInfo?.EnableAffPromotionOddsBoost === true
			&& user?.PromotionOddsBoostInfo?.GiftOddsBoostTicketCount !== undefined
			&& [3, 4, 5, 8, 43].includes(ticket.LeagueId)
			&& isVoucherCommonBaseCondition && isVoucherSupportedSport;
	}, [user?.PromotionOddsBoostInfo, ticket.LeagueId, isVoucherCommonBaseCondition, isVoucherSupportedSport]);

	// 10 Valid for Soccer & Basketball (Excl. HDP/OU), lbl_newRFBhint01
	const canUseNewRiskFreeBetVoucher = useMemo(() => {
		return enableRewardSystem && isVoucherSupportedSport && isNewRiskFreeBetVoucherSupported;
	}, [enableRewardSystem, isVoucherSupportedSport, isNewRiskFreeBetVoucherSupported]);

	// 14 目前沒在用
	const canUseGiftRiskFreeBet = Boolean(user?.IsValidPromotionVoucherUser)
		&& fifaCwcLeagueIds.includes(ticket.LeagueId);


	const canFetchOddsBoostVouchers = Boolean(user)
		&& (user?.PromotionOddsBoostInfo?.GiftOddsBoostTicketCount || 0) > 0
		&& (
			user?.PromotionOddsBoostInfo?.EnableAffPromotionOddsBoost === true
			|| user?.PromotionOddsBoostInfo?.EnablePromotionOddsBoost === true
		);

	const canFetchVouchers = !isDisableVoucher && (enableFreeBetVoucher || enableRewardSystem || canFetchOddsBoostVouchers || canUseGiftRiskFreeBet);


	// 舊邏輯 先保留比對
	// const canFetchRewardVouchers = (enableRewardSystem || canUsePromotionOddsBoost || canUseAffPromotionOddsBoost) && 
	// 	isVoucherSupportedSport &&
	// 	isVoucherCommonBaseCondition;
	// const canFetchVouchers = canFetchRewardVouchers || canUseGiftRiskFreeBet;

	const voucherAvailabilityRules = useMemo<Partial<Record<number, VoucherAvailability>>>(() => ({
		[VoucherTypeEnum.OddsBooster]: {
			isEnabled: canUseOddsBoostVoucher,
			disableMessage: 'lbl_Obphint01',
		},
		[VoucherTypeEnum.FreeOddsBooster]: {
			isEnabled: Boolean(canUsePromotionOddsBoost),
			disableMessage: 'lbl_Obphint01',
		},
		[VoucherTypeEnum.AffPromotionOddsBoost]: {
			isEnabled: Boolean(canUseAffPromotionOddsBoost),
			disableMessage: 'lbl_Obphint01',
		},
		[VoucherTypeEnum.NewRiskFreeBet]: {
			isEnabled: Boolean(canUseNewRiskFreeBetVoucher),
			disableMessage: 'lbl_newRFBhint01',
		},
		[VoucherTypeEnum.GiftRiskFreeBet]: {
			isEnabled: canUseGiftRiskFreeBet,
			disableMessage: 'lbl_newRFBhint01',
		},
	}), [
		canUseOddsBoostVoucher,
		canUsePromotionOddsBoost,
		canUseAffPromotionOddsBoost,
		canUseNewRiskFreeBetVoucher,
		canUseGiftRiskFreeBet,
	]);

	const getVoucherAvailability = useCallback((voucherType: number): VoucherAvailability => {
		const voucherAvailability = voucherAvailabilityRules[voucherType];

		if (!voucherAvailability || voucherAvailability.isEnabled) {
			return {
				isEnabled: true,
				disableMessage: null,
			};
		}

		return {
			isEnabled: false,
			disableMessage: voucherAvailability.disableMessage,
		};
	}, [voucherAvailabilityRules]);

	useEffect(() => {
		const fetchVouchers = async () => {
			try {
				if (!canFetchVouchers) {
					setShouldFetchFreeBetVoucherCondition(false);
					setRawVouchers([]);
					return;
				}

				const allVouchers = (await getWillBeUsedVouchers(
					RewardUseVoucherEnum.filterForGalaxyMarsBetSlip
				)).filter(voucher => voucher.VoucherStatus !== PromotionSuiteVoucherStatus.NotYetEffective);

				const hasFreeBetVoucher = allVouchers.some(
					voucher => voucher.VoucherType === VoucherTypeEnum.FreeBet
				);
				setShouldFetchFreeBetVoucherCondition(hasFreeBetVoucher);
				setRawVouchers(allVouchers);
			} catch (error) {
				console.error('Error fetching vouchers:', error);
			}
		};

		fetchVouchers();
	}, [
		canFetchVouchers,
	]);

	const vouchersWithValidation = useMemo(() => {
		return rawVouchers.map(voucher => {
			const voucherAvailability = getVoucherAvailability(voucher.VoucherType);

			// get free bet voucher condition
			const freeBetVoucherCondition =
				voucher.VoucherType === VoucherTypeEnum.FreeBet
					? freeBetConditionMap.get(getFreeBetConditionKey(voucher.VoucherDetailId, voucher.VoucherSystemVersion))
					: undefined;

			// validate free bet voucher condition
			const freeBetConditionValidation =
				(voucher.VoucherType === VoucherTypeEnum.FreeBet && ticket && isFreeBetConditionFetched && freeBetVoucherCondition)
					? validateFreeBetCondition(freeBetVoucherCondition, ticket, oddsType)
					: undefined;

			const isFreeBet = voucher.VoucherType === VoucherTypeEnum.FreeBet && freeBetConditionValidation;

			const isEnabled = isFreeBet 
				? freeBetConditionValidation.isEnabled 
				: voucherAvailability.isEnabled;

			const disableMessage = isFreeBet ? null : voucherAvailability.disableMessage;

			const disableMessages = isFreeBet && freeBetConditionValidation.disableMessages?.length
				? freeBetConditionValidation.disableMessages
				: undefined;

			return {
				...voucher,
				isEnabled,
				disableMessage,
				...(disableMessages != null ? { disableMessages } : {}),
				disableMessageData: freeBetConditionValidation?.disableMessageData || undefined,
				expireDateDisplay: formatExpireDateForDisplay(voucher.ExpireDate),
			};
		});
	}, [
		rawVouchers,
		getVoucherAvailability,
		freeBetConditionMap,
		ticket,
		isFreeBetConditionFetched,
		oddsType,
	]);

	const vouchers = useMemo(() => {
		const filteredVouchers = enableFreeBetVoucher
			? vouchersWithValidation
			: vouchersWithValidation.filter(
					v => v.VoucherType !== VoucherTypeEnum.FreeBet
				);
		return sortVouchers(filteredVouchers);
	}, [vouchersWithValidation, enableFreeBetVoucher]);

	const vouchersState = useMemo(() => ({ 
		vouchers, 
		freeBetConditionMap 
	}), [vouchers, freeBetConditionMap]);

	const value = {
		...vouchersState,
	};

	return (
		<VoucherContext.Provider value={value}>
			{children}
		</VoucherContext.Provider>
	);
};

export const useVouchers = () => {
	const context = useContext(VoucherContext);

	if (context === undefined) {
		throw new Error('useVouchers must be used within a VoucherContextProvider');
	}

	return context;
};

export const useEnableFreeBetVoucher = () => {
	const { SiteConfigsData } = useGetSiteConfigs();
	
	const enableFreeBetVoucher = useMemo(() => {
		return SiteConfigsData?.EnableVoucherList?.includes(VoucherTypeEnum.FreeBet) || false;
	}, [SiteConfigsData?.EnableVoucherList]);
	
	return enableFreeBetVoucher;
};